/**
 * MigrationHub (/switch-from-quickbooks) — three-door entry redesign.
 *
 * Lands the user on a door picker (Fresh Start / Match my reports /
 * Full historical replay). Each door has its own guided wizard with
 * exact click-by-click QuickBooks export instructions so a non-
 * accountant user can finish without help.
 *
 * The legacy Cost Calculator + Manual Checklist + "Import" tab still
 * live on this page but are demoted below the doors as "Other tools."
 *
 * Replaces the prior MigrationHub which buried the working Mid-Year
 * Cutover Wizard behind a non-default tab and exposed the broken
 * "Import → Transactions" preview-only path as the primary CTA.
 */
import { useState } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowRightLeft, ArrowRight, Sparkles, BarChart3, FileSpreadsheet,
  Clock, Users, CheckCircle2, AlertCircle, ChevronRight,
  Calculator, ClipboardList,
} from 'lucide-react';
import { cn } from '../lib/utils';
import FreshStartFlow from '../components/migration/FreshStartFlow';
import PnLBalanceSheetFlow from '../components/migration/PnLBalanceSheetFlow';
import MidYearCutoverWizard from '../components/migration/MidYearCutoverWizard';
import MigrationChecklist from '../components/migration/MigrationChecklist';

const DOORS = [
  {
    id: 'fresh',
    icon: Sparkles,
    iconClass: 'text-emerald-500',
    badge: 'Recommended',
    badgeTone: 'emerald',
    title: 'Fresh Start',
    eta: '~3 minutes',
    summary: 'Pick a date. Connect your bank. Done.',
    bullets: [
      'No CSV exports, no spreadsheets',
      'Bank transactions auto-pull from cutover date forward',
      'Historical periods stay in QuickBooks for reference',
    ],
    bestFor: 'Small businesses that don\'t need QB history inside CountBucks.',
  },
  {
    id: 'pnl_bs',
    icon: BarChart3,
    iconClass: 'text-sky-500',
    badge: 'Most common',
    badgeTone: 'sky',
    title: 'Match my QuickBooks reports',
    eta: '~10 minutes',
    summary: 'Upload your P&L and Balance Sheet. We create your opening balances.',
    bullets: [
      'Uses the two reports every QB user knows how to find',
      'AI maps QB account names to your CountBucks chart of accounts',
      'Net income auto-flows into Retained Earnings — JE always balances',
    ],
    bestFor: 'You want CountBucks reports to match QB from day one.',
  },
  {
    id: 'full',
    icon: FileSpreadsheet,
    iconClass: 'text-violet-500',
    badge: 'CPA-led',
    badgeTone: 'violet',
    title: 'Full historical replay',
    eta: '~20 minutes',
    summary: 'Trial Balance + Transaction Detail import. Most thorough.',
    bullets: [
      'Posts one JE per transaction since cutover',
      'Best for clean year-end audits',
      'Recommended if you have a Trial Balance CSV',
    ],
    bestFor: 'Working with your accountant on a clean cutover.',
  },
];

function DoorCard({ door, onPick }) {
  const Icon = door.icon;
  return (
    <button
      type="button"
      onClick={() => onPick(door.id)}
      className={cn(
        'text-left rounded-2xl border bg-card p-5 transition-all w-full h-full',
        'hover:border-emerald-500/50 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30'
      )}
      data-testid={`door-${door.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={cn('w-10 h-10 rounded-xl bg-muted flex items-center justify-center', door.iconClass)}>
          <Icon className="w-5 h-5" />
        </div>
        {door.badge && (
          <Badge
            variant="outline"
            className={cn(
              'text-[10px] font-medium',
              door.badgeTone === 'emerald' && 'border-emerald-500/40 text-emerald-600 bg-emerald-500/10',
              door.badgeTone === 'sky' && 'border-sky-500/40 text-sky-600 bg-sky-500/10',
              door.badgeTone === 'violet' && 'border-violet-500/40 text-violet-600 bg-violet-500/10',
            )}
          >
            {door.badge}
          </Badge>
        )}
      </div>
      <h3 className="text-lg font-semibold mt-4">{door.title}</h3>
      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
        <Clock className="w-3 h-3" /> {door.eta}
      </p>
      <p className="text-sm text-foreground mt-3">{door.summary}</p>
      <ul className="mt-3 space-y-1.5 text-xs text-muted-foreground">
        {door.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
            <span className="leading-relaxed">{b}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 pt-3 border-t text-xs text-muted-foreground italic">
        Best for: {door.bestFor}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs text-emerald-600 font-medium">Start this path</span>
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </div>
    </button>
  );
}

export default function MigrationHub() {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.workspace_id;
  const [chosenDoor, setChosenDoor] = useState(null);
  const [showOtherTools, setShowOtherTools] = useState(false);

  // ── Door router ─────────────────────────────────────────────────
  if (chosenDoor && workspaceId) {
    const back = () => setChosenDoor(null);
    if (chosenDoor === 'fresh') {
      return (
        <div className="max-w-5xl mx-auto space-y-6 px-4 sm:px-6 py-6">
          <FreshStartFlow workspaceId={workspaceId} onBack={back} />
        </div>
      );
    }
    if (chosenDoor === 'pnl_bs') {
      return (
        <div className="max-w-5xl mx-auto space-y-6 px-4 sm:px-6 py-6">
          <PnLBalanceSheetFlow workspaceId={workspaceId} onBack={back} />
        </div>
      );
    }
    if (chosenDoor === 'full') {
      return (
        <div className="max-w-5xl mx-auto space-y-6 px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-violet-500" />
              Full historical replay
            </h2>
            <Button variant="ghost" size="sm" onClick={back} data-testid="full-door-back">
              ← Back to options
            </Button>
          </div>
          <MidYearCutoverWizard workspaceId={workspaceId} />
        </div>
      );
    }
  }

  // ── Door picker (landing state) ─────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto space-y-8 px-4 sm:px-6 py-6" data-testid="migration-hub">
      {/* Hero */}
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-500/15 mb-3">
          <ArrowRightLeft className="w-6 h-6 text-emerald-500" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Switch from QuickBooks</h1>
        <p className="text-muted-foreground mt-2 leading-relaxed">
          Three ways to make the switch — pick what matches the time you have and the level of detail you need.
          You can always come back to add more later.
        </p>
      </div>

      {/* Door grid */}
      {!workspaceId ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
            Select a workspace to start switching from QuickBooks.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {DOORS.map((d) => (
            <DoorCard key={d.id} door={d} onPick={setChosenDoor} />
          ))}
        </div>
      )}

      {/* FAQ */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Not sure which to pick?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <FaqRow
            q="I just want to start using CountBucks for my new transactions."
            a="Pick Fresh Start. It's the simplest — no exports needed."
          />
          <FaqRow
            q="I want my CountBucks reports to look like my QB reports from day one."
            a="Pick Match my QuickBooks reports. You'll export two CSVs (P&L + Balance Sheet) and we do the rest."
          />
          <FaqRow
            q="My CPA is doing a clean year-end migration and wants every transaction."
            a="Pick Full historical replay. Best if you have a Trial Balance export from QB."
          />
          <FaqRow
            q="What's the difference between P&L and Balance Sheet?"
            a="P&L (Profit and Loss) shows income and expenses for a period. Balance Sheet shows what you own (assets) and owe (liabilities) at a point in time. QuickBooks has both under Reports → Business overview."
          />
        </CardContent>
      </Card>

      {/* Other tools — collapsed by default */}
      <div className="text-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowOtherTools((s) => !s)}
          data-testid="toggle-other-tools"
          className="text-muted-foreground"
        >
          {showOtherTools ? 'Hide' : 'Show'} other tools (cost calculator · manual checklist)
        </Button>
      </div>

      {showOtherTools && workspaceId && (
        <div className="space-y-4 pt-2" data-testid="other-tools-panel">
          <MigrationChecklist workspaceId={workspaceId} />
        </div>
      )}
    </div>
  );
}

function FaqRow({ q, a }) {
  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <p className="font-medium text-sm">{q}</p>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{a}</p>
    </div>
  );
}
